import { Head } from "@inertiajs/react"
import { useCallback, useEffect, useState } from "react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { BrandContextStrip } from "@/components/brand/brand-context-strip"
import { BrandCoverBand } from "@/components/brand/brand-cover-band"
import { BrandLogo } from "@/components/brand-logo"
import { BrandSplash } from "@/components/brand-splash"
import { CrewSwitcher } from "@/components/crew-switcher"
import { FloatingCta } from "@/components/invites/floating-cta"
import { InviteValueCards } from "@/components/invites/invite-value-cards"
import { LivingCrewTeaser } from "@/components/invites/living-crew-teaser"
import { RecognisedEmailLanding } from "@/components/invites/recognised-email-landing"
import { RecognisedJoinBar } from "@/components/invites/recognised-join-bar"
import { RoleMismatchLanding } from "@/components/invites/role-mismatch-landing"
import { IdentityMismatchView } from "@/components/onboarding/identity-mismatch-view"
import { useCurrentContext } from "@/hooks/use-current-context"
import PublicLayout from "@/layouts/public-layout"
import { initI18n } from "@/lib/i18n/client"
import { resolvePublicLocale } from "@/lib/i18n-public"
import { pickTaglines } from "@/lib/pick-taglines"
import { signInPath } from "@/routes"
import type { InviteLandingProps } from "@/types/invites"

/**
 * OKS-21 — Public invite landing page (`/i/:token`).
 *
 * **Single shell, content swap by `data-state`.** The server-side
 * state collapse (`valid` | `invalid` | `identity_mismatch`) lands
 * on the root `<div>` as `data-state` so QA's E10 negative
 * assertion can rely on it as the single source of truth (no
 * stage-specific testids that could leak terminal-reason
 * disclosure).
 *
 * **Mobile-first.** 402px reference (OKS-457 V2 — bumped from the
 * OKS-21 390px; the cover is full-bleed edge-to-edge with no clamp).
 * Dark hero band + light content; floating CTA full-width.
 *
 * **OKS-457 — §00 brand splash.** On the FIRST `valid` view for a device
 * (server `show_splash` prop, backed by the per-invite
 * `oks_splash_seen_<id>` cookie), an immersive `<BrandSplash>` overlay
 * paints above §01 and cross-dissolves away (TRD KD-1). The valid §01
 * composition is brand-led: sticky brand strip, identity cover with a
 * "Hello {name} 👋" greeting + inviter line, living-crew social-proof
 * teaser, charter value cards, and a floating Accept bar.
 *
 * **No `AppLayout`.** This page is unauthenticated — the auth shell
 * assumes `Current.user` and `BrandContext` which don't exist on the
 * public route. We use the dedicated `<PublicLayout>` instead.
 *
 * **Locale resolution.** Anonymous visitor → no shared `locale`
 * prop. We resolve via `resolvePublicLocale()`:
 *   `?locale=`/`?lang=` → `brand.locale` → `navigator.language` → `"fr"`.
 * OKS-536 — `brand.locale` outranks the visitor's browser language:
 * this is a brand-presenting-itself surface (the candidate has no
 * locale preference yet), so a `fr` brand renders French even in an
 * `en` browser, matching the server's brand-derived render. Re-init
 * i18n on mount so the first paint after hydration uses the resolved
 * locale with no flicker.
 *
 * **P0 brand-isolation.** The Inertia prop `brand` shape is
 * `{name, logo_url, locale}` — no IDs, no slugs. Backend's
 * `landing_props` helper enforces this; we mirror by not destructuring
 * any other fields off `brand`. The OKS-347 cover image is a
 * **top-level sibling prop** (`cover_image_url`), deliberately NOT
 * nested in `brand` so the disclosure-locked brand shape is preserved.
 */

export default function InviteLanding({
  brand,
  state,
  show_splash,
  crew_count = null,
  views_this_month = null,
  cover_image_url,
  splash_cover_image_url,
  cta_url,
  cta_token = null,
  inviter = null,
  value_cards = null,
  candidate_name = null,
  accept_url = null,
  sign_out_url = null,
  current_role,
  mismatch_context,
  mismatch_handle,
  mismatch_support_url,
}: InviteLandingProps) {
  const { t } = useTranslation()
  const { currentUser, availableBrands } = useCurrentContext()

  // OKS-675 — the brand welcome composition renders on `valid` AND on the
  // recognised-member welcome sub-variants (logged-in match → `accept_url`;
  // logged-out → `cta_url`). The recognised wrong-account sub-variant ships
  // neither and falls through to the dead-end card.
  const isWelcome =
    state === "valid" ||
    (state === "recognised_email" && (accept_url !== null || cta_url !== null))

  // OKS-457 / OKS-675 — splash mounts ONLY on a first WELCOME view (server
  // gates `show_splash`). Local state lets `<BrandSplash>` cross-dissolve
  // away and unmount on completion; §01 is always painted beneath it.
  const [splashActive, setSplashActive] = useState(
    () => show_splash && isWelcome,
  )
  const dismissSplash = useCallback(() => setSplashActive(false), [])

  // OKS-738 — pick a random 2–3 distinct crew-perk taglines ONCE on mount
  // (stable for the splash's lifetime). The random pick lives in the pure
  // `pickTaglines` helper so `<BrandSplash>` stays deterministic; it just
  // cross-fades the ordered array we pass down. Invite-scoped: the switch
  // surface never passes `taglines`, so its dots/timing are unchanged.
  const [splashTaglines] = useState(() =>
    pickTaglines(
      t("invites.landing.splash.taglines", {
        returnObjects: true,
      }) as string[],
    ),
  )

  // Re-init i18n with the resolved public locale on mount. Effect
  // (not module-side) so SSR keeps the `brand.locale` default and
  // hydration applies the browser-derived locale once.
  useEffect(() => {
    const locale = resolvePublicLocale({ brandLocale: brand.locale })
    initI18n({ locale })
  }, [brand.locale])

  return (
    <PublicLayout>
      {/*
        OKS-156 — the page is the single owner of the tab title. The shared
        <IdentityMismatchView> no longer declares its own <Head>, so we branch
        the title here: the identity_mismatch state (both `anon` and
        `recognised` mismatch_context variants) keeps the specific mismatch
        title; every other state keeps the generic invite headline. Net
        per-state title text is unchanged from prior (override) behavior.
      */}
      <Head>
        <title>
          {t(
            state === "identity_mismatch"
              ? "wizard.identity_mismatch.head_title"
              : "invites.landing.headline",
          )}
        </title>
      </Head>
      {/*
        OKS-457 — §00 brand splash overlay. Mounts ONLY on a first `valid`
        view (server `show_splash` gate); cross-dissolves away and unmounts
        via `dismissSplash`. Rendered as a sibling above the landing so §01
        is fully painted beneath it (TRD KD-1 overlay, not a route).
      */}
      {splashActive && isWelcome && (
        <BrandSplash
          brandName={brand.name}
          logoUrl={brand.logo_url}
          coverUrl={cover_image_url}
          // OKS-857 — portrait splash background; precedence over the
          // landscape cover (falls back to it, then dark, when null).
          splashCoverUrl={splash_cover_image_url}
          taglines={splashTaglines}
          minHoldMs={3500}
          onComplete={dismissSplash}
        />
      )}
      <div
        data-testid="invite-landing"
        data-state={state}
        className="flex min-h-svh flex-col"
      >
        {/*
          OKS-457 / OKS-675 — welcome renders (`valid` + recognised-member
          welcome sub-variants) get the brand-led §01 composition
          (`<WelcomeLanding>`): sticky brand strip → identity cover with
          greeting + inviter line → living-crew teaser → value cards →
          floating CTA bar. The CTA forks by session: logged-out recognised
          + `valid` → native "Continue with TikTok" (`<FloatingCta>`);
          logged-in recognised match → one-tap join (`<RecognisedJoinBar>`).
          Every NON-welcome state keeps the OKS-347 stacked chrome verbatim
          (cover band on top, logo hero below, shared content container) so
          those dead-end states are visually unchanged (PRD out-of-scope).
          `cover_image_url` is null when no cover / no resolvable brand →
          dark monogram fallback.
        */}
        {isWelcome ? (
          <WelcomeLanding
            brand={brand}
            cover_image_url={cover_image_url}
            inviter={inviter}
            candidate_name={candidate_name}
            value_cards={value_cards ?? []}
            crew_count={crew_count}
            views_this_month={views_this_month}
            cta={
              state === "recognised_email" && accept_url ? (
                <RecognisedJoinBar
                  accept_url={accept_url}
                  brand_name={brand.name}
                  sign_out_url={sign_out_url}
                />
              ) : (
                <FloatingCta
                  ctaUrl={cta_url}
                  ctaToken={cta_token}
                  testId={
                    state === "recognised_email"
                      ? "invite-landing-recognised-tiktok"
                      : undefined
                  }
                />
              )
            }
          />
        ) : (
          <>
            <BrandCoverBand coverUrl={cover_image_url} brandName={brand.name} />
            <Hero brand={brand} />
            <div
              data-testid="invite-landing-content"
              className="flex flex-1 flex-col gap-6 px-6 py-8 sm:max-w-md sm:self-center sm:py-10"
            >
              {state === "invalid" && <InvalidContent />}
              {state === "identity_mismatch" && (
                // OKS-24 — fork on `mismatch_context` per UX §12.1. Server
                // ships `"anon"` for the OKS-21 original path or `"recognised"`
                // for Flow E (second-brand mismatch BEFORE membership
                // mutation). FE never decides — purely renders.
                //
                // For `recognised`, the CTA is a server-resolved mailto. If
                // server fails to ship `mismatch_support_url`, we omit the
                // CTA entirely (UX review round 2 — silent `signInPath()`
                // fallback dropped; better to fail visibly than to mis-direct).
                // For `anon`, `signInPath()` is the OKS-21 retry contract.
                <>
                  <IdentityMismatchView
                    context={mismatch_context ?? "anon"}
                    handle={mismatch_handle ?? ""}
                    cta_url={
                      mismatch_context === "recognised"
                        ? (mismatch_support_url ?? null)
                        : signInPath()
                    }
                    brand_name={brand.name}
                  />
                  <p
                    data-testid="invite-landing-error-contact-hint"
                    className="text-muted-foreground text-sm"
                  >
                    {t("invites.landing.error.contact_hint")}
                  </p>
                </>
              )}
              {/*
                OKS-675 — recognised_email only reaches this dead-end branch
                for the WRONG-ACCOUNT sub-variant (logged in as a different
                user; server ships `sign_out_url` only, no welcome props, no
                email). The welcome sub-variants render `<WelcomeLanding>`
                above. `<RecognisedEmailLanding>` is now the email-free
                wrong-account logout card.
              */}
              {state === "recognised_email" && sign_out_url && (
                <>
                  {/*
                OKS-24 F17 — wire <CrewSwitcher /> into the recognised
                landing when the matching session has ≥2 memberships.
                Renders nothing if ineligible (CrewSwitcher self-gates).
                Useful escape hatch — the creator can switch to an
                existing crew and defer the new invite.
              */}
                  {currentUser?.role === "creator" &&
                    availableBrands.length >= 2 && (
                      <div className="self-end">
                        <CrewSwitcher />
                      </div>
                    )}
                  <RecognisedEmailLanding sign_out_url={sign_out_url} />
                  <p
                    data-testid="invite-landing-error-contact-hint"
                    className="text-muted-foreground self-center text-sm"
                  >
                    {t("invites.landing.error.contact_hint")}
                  </p>
                </>
              )}
              {state === "role_mismatch" && (
                <RoleMismatchLanding current_role={current_role ?? "user"} />
              )}
            </div>
          </>
        )}

        <Footer brand_name={brand.name} />
      </div>
    </PublicLayout>
  )
}

interface HeroProps {
  brand: InviteLandingProps["brand"]
}

function Hero({ brand }: HeroProps) {
  const { t } = useTranslation()
  return (
    <section
      data-testid="invite-landing-hero"
      className="flex flex-col items-center gap-4 bg-zinc-950 px-6 pt-14 pb-10 text-center text-white dark:bg-zinc-900"
    >
      {/*
        OKS-303 — `<BrandLogo>` owns the `<img>` branch and the
        `onError`-driven swap to `<BrandInitialMonogram>` (UX Spec §3
        Error state). `monogramClassName` overrides the monogram's
        default `bg-foreground/10 text-foreground` (which would render
        dark-on-dark inside the `bg-zinc-950` hero) with white-on-overlay
        tokens that mirror the `<img>` branch's tile chrome — `text-3xl`
        keeps the ~4:1 tile-to-text ratio OKS-266 established.

        The container `data-testid="invite-landing-brand-logo"` is stable
        across both branches; `-image` and `-fallback` testids
        discriminate which branch rendered for QA assertions (TRD KD-3).
      */}
      <BrandLogo
        logoUrl={brand.logo_url}
        name={brand.name}
        className="size-20 rounded-full bg-white/10 object-contain p-2 ring-2 ring-white/15"
        monogramClassName="text-3xl text-white"
        alt={t("invites.landing.brand_logo_alt", { brand_name: brand.name })}
        testId="invite-landing-brand-logo"
      />

      <div className="flex flex-col items-center gap-1">
        <span
          data-testid="invite-landing-brand-name"
          className="text-base font-medium tracking-wide text-white/85"
        >
          {brand.name}
        </span>
        <h1
          data-testid="invite-landing-headline"
          className="text-2xl leading-tight font-semibold text-pretty sm:text-3xl"
        >
          {t("invites.landing.headline")}
        </h1>
      </div>
    </section>
  )
}

interface WelcomeLandingProps {
  brand: InviteLandingProps["brand"]
  cover_image_url: string | null
  inviter: InviteLandingProps["inviter"]
  candidate_name: string | null
  value_cards: NonNullable<InviteLandingProps["value_cards"]>
  crew_count: number | null
  views_this_month: number | null
  /** OKS-675 — the fixed bottom CTA bar. Forks by session: `<FloatingCta>`
   *  (native "Continue with TikTok") for `valid` + logged-out recognised;
   *  `<RecognisedJoinBar>` (one-tap join) for a logged-in recognised match. */
  cta: ReactNode
}

/**
 * OKS-457 / OKS-675 — Brand-led §01 invite-landing composition, shared by
 * the `valid` state and the recognised-member welcome sub-variants (UX Spec
 * §2). Top→bottom: sticky brand strip → identity cover (greeting + inviter
 * line) → living-crew teaser → "what you get" intro → charter value cards →
 * fixed CTA bar (`cta` slot). The content column carries generous bottom
 * padding (`pb-32`) so the fixed CTA bar never occludes the last card (UX
 * Spec §6). Personalisation is via `candidate_name` (first name) — NO email
 * is ever rendered (privacy P0).
 */
function WelcomeLanding({
  brand,
  cover_image_url,
  inviter,
  candidate_name,
  value_cards,
  crew_count,
  views_this_month,
  cta,
}: WelcomeLandingProps) {
  return (
    <>
      <BrandContextStrip brandName={brand.name} logoUrl={brand.logo_url} />
      <ValidCover
        brand={brand}
        cover_image_url={cover_image_url}
        inviter={inviter}
        candidate_name={candidate_name}
      />
      <div
        data-testid="invite-landing-content"
        className="flex flex-1 flex-col gap-6 px-6 pt-8 pb-32 sm:max-w-md sm:self-center sm:pt-10"
      >
        <LivingCrewTeaser
          brandName={brand.name}
          crewCount={crew_count}
          viewsThisMonth={views_this_month}
        />
        <IntroSection />
        <InviteValueCards brandName={brand.name} cards={value_cards} />
      </div>
      {cta}
    </>
  )
}

interface ValidCoverProps {
  brand: InviteLandingProps["brand"]
  cover_image_url: string | null
  inviter: InviteLandingProps["inviter"]
  candidate_name: string | null
}

/**
 * OKS-457 — Brand-led identity cover (UX Spec §2.2): the brand cover
 * image (or dark fallback) + bottom-up gradient carrying the centred
 * brand logo, the personalised "Hello {first name} 👋" greeting (the
 * page's primary heading), and the inviter line "{inviter} invited you
 * to join {brand}'s crew". The inviter PHOTO is intentionally dropped
 * (brand-led — ticket scope); only the name stays in copy. When no
 * inviter resolves, the inviter line is omitted.
 */
function ValidCover({
  brand,
  cover_image_url,
  inviter,
  candidate_name,
}: ValidCoverProps) {
  const { t } = useTranslation()
  // Greeting uses the invitee's FIRST name (split client-side from the
  // server `candidate_name`). Falls back to a name-less greeting so the
  // heading always renders (Edge: candidate with no name).
  const firstName = candidate_name?.trim().split(/\s+/)[0]
  const greeting = firstName
    ? t("invites.landing.greeting", { name: firstName })
    : t("invites.landing.greeting_no_name")

  return (
    <BrandCoverBand
      variant="identity"
      coverUrl={cover_image_url}
      brandName={brand.name}
    >
      {/* `<BrandLogo>` keeps the OKS-303 testid contract (-image /
          -fallback branches) on the identity overlay. */}
      <BrandLogo
        logoUrl={brand.logo_url}
        name={brand.name}
        className="size-12 rounded-2xl bg-white/10 object-contain p-1.5 ring-1 ring-white/15"
        monogramClassName="text-xl text-white"
        alt={t("invites.landing.brand_logo_alt", { brand_name: brand.name })}
        testId="invite-landing-brand-logo"
      />
      <h1
        data-testid="invite-landing-headline"
        className="text-[26px] leading-tight font-bold text-pretty text-white"
      >
        {greeting}
      </h1>
      {inviter && (
        <p
          data-testid="invite-landing-inviter"
          className="max-w-xs text-pretty text-white/90"
        >
          {t("invites.landing.invited_by", {
            inviter: inviter.name,
            brand: brand.name,
          })}
        </p>
      )}
    </BrandCoverBand>
  )
}

/**
 * OKS-457 — "What you get as crew" intro block (UX Spec §2.4). Static
 * brand-default copy that frames the charter value cards below it.
 */
function IntroSection() {
  const { t } = useTranslation()
  return (
    <section className="flex flex-col gap-1">
      <h2 className="text-foreground text-[22px] leading-tight font-semibold">
        {t("invites.landing.intro.heading")}
      </h2>
      <p className="text-muted-foreground text-[15px]">
        {t("invites.landing.intro.body")}
      </p>
    </section>
  )
}

function InvalidContent() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="invite-landing-error-invalid"
      className="flex flex-col gap-4"
      role="status"
    >
      <h2 className="text-foreground text-xl font-semibold">
        {t("invites.landing.error.invalid.heading")}
      </h2>
      <p className="text-muted-foreground text-base leading-relaxed">
        {t("invites.landing.error.invalid.body")}
      </p>
      <p
        data-testid="invite-landing-error-contact-hint"
        className="text-muted-foreground text-sm"
      >
        {t("invites.landing.error.contact_hint")}
      </p>
    </div>
  )
}

// OKS-24 — `MismatchContent` removed. The identity-mismatch shell is now
// owned by the shared `<IdentityMismatchView>` (under
// `components/onboarding/identity-mismatch-view.tsx`), driven by
// `data-mismatch-context` per UX §12.1. The OKS-21 anonymous path still
// renders here via `mismatch_context="anon"` and gets the same retry CTA
// shape via that shared component (cta_url=signInPath() — the dedicated
// component branches on context to pick the right CTA target + label).

function Footer({ brand_name }: { brand_name: string }) {
  const { t } = useTranslation()
  const { privacyUrl } = useCurrentContext()

  // OKS-846 — the privacy affordance (specced in OKS-21 UX §2.3 "footer:
  // brand legal name + privacy link") is restored now that a real Privacy
  // Policy page exists (on oks.studio). It was removed in OKS-21 Phase 3
  // only because `/privacy` 404'd on the dead legacy domain. The URL
  // comes from the shared `privacyUrl` context prop (env `PRIVACY_URL`
  // override, else the oks.studio default) — a non-blank string on every
  // request incl. anon, so the link is safe to render here. Guarded on
  // presence so a future empty value never emits a bare `href`.
  return (
    <footer className="text-muted-foreground flex flex-col items-center gap-1 px-6 py-6 text-center text-xs">
      <span>{brand_name}</span>
      {privacyUrl ? (
        <a
          data-testid="invite-landing-footer-privacy"
          href={privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground underline underline-offset-2"
        >
          {t("invites.landing.footer.privacy")}
        </a>
      ) : null}
    </footer>
  )
}
